import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Initial Consultation',
    description: 'We assess your property, how it\'s titled, existing mortgages, homestead status, and your overall estate planning goals to ensure a Lady Bird Deed is the right fit.',
    details: [
      'Property title review',
      'Mortgage analysis',
      'Homestead qualification',
      'Estate planning alignment'
    ]
  },
  {
    number: '02',
    title: 'Choose Your Beneficiaries',
    description: 'You decide who will inherit your property upon your passing. This can be one or multiple individuals, and you can include contingent beneficiaries.',
    details: [
      'Name primary beneficiaries',
      'Add contingent beneficiaries',
      'Address special situations',
      'Plan for multiple heirs'
    ]
  },
  {
    number: '03',
    title: 'Professional Deed Drafting',
    description: 'We draft your Lady Bird Deed with Florida-specific language that preserves your full control and revocation rights. DIY templates often fail to include crucial provisions.',
    details: [
      'Florida-compliant language',
      'Revocation rights preserved',
      'Full control maintained',
      'Professional review'
    ]
  },
  {
    number: '04',
    title: 'Sign, Notarize & Record',
    description: 'The deed is signed, notarized according to Florida requirements, and officially recorded in your county\'s public records. Your protection is now in place.',
    details: [
      'In-person or remote signing',
      'Notarization completed',
      'County recording filed',
      'Confirmation provided'
    ]
  },
];

const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="relative py-24 lg:py-32 bg-[#0f0f0f]" ref={sectionRef}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#c9a87c]/3 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-16 lg:mb-20">
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#c9a87c] mb-4 block">
            The Process
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#faf9f7] mb-6">
            How a Lady Bird Deed <span className="text-gradient-gold">Works in Florida</span>
          </h2>
          <p className="font-sans text-lg text-[#faf9f7]/60">
            Establishing a Lady Bird Deed is a straightforward process when handled by an experienced 
            estate planning attorney. Here's what to expect.
          </p>
        </div>

        {/* Steps */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Step List */}
          <div className="space-y-4">
            {steps.map((step, index) => (
              <button
                key={index}
                onClick={() => setActiveStep(index)}
                className={`w-full text-left p-6 rounded-lg transition-all duration-500 ${
                  activeStep === index
                    ? 'bg-gradient-to-r from-[#c9a87c]/20 to-[#c9a87c]/5 border border-[#c9a87c]/30'
                    : 'bg-[#141414]/50 border border-transparent hover:border-[#c9a87c]/10'
                } ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <span className={`step-number transition-colors duration-300 ${
                    activeStep === index ? 'text-[#c9a87c]' : 'text-[#c9a87c]/20'
                  }`}>
                    {step.number}
                  </span>
                  <div>
                    <h3 className={`font-serif text-xl mb-2 transition-colors duration-300 ${
                      activeStep === index ? 'text-[#c9a87c]' : 'text-[#faf9f7]'
                    }`}>
                      {step.title}
                    </h3>
                    <p className="font-sans text-sm text-[#faf9f7]/50 line-clamp-2">
                      {step.description}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Active Step Detail */}
          <div 
            className={`relative ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
            style={{ transitionDelay: '400ms', transition: 'all 0.6s ease' }}
          >
            <div className="sticky top-32">
              <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 lg:p-10">
                {/* Step Number */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a87c]/10 mb-6">
                  <span className="font-serif text-sm text-[#c9a87c]">Step {steps[activeStep].number}</span>
                </div>

                {/* Title */}
                <h3 className="font-serif text-2xl lg:text-3xl text-[#faf9f7] mb-4">
                  {steps[activeStep].title}
                </h3>

                {/* Description */}
                <p className="font-sans text-[#faf9f7]/70 mb-8 leading-relaxed">
                  {steps[activeStep].description}
                </p>

                {/* Details */}
                <div className="space-y-3">
                  {steps[activeStep].details.map((detail, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-3"
                    >
                      <CheckCircle className="w-5 h-5 text-[#c9a87c] flex-shrink-0" />
                      <span className="font-sans text-sm text-[#faf9f7]/80">{detail}</span>
                    </div>
                  ))}
                </div>

                {/* Decorative element */}
                <div className="absolute -bottom-4 -right-4 w-32 h-32 border border-[#c9a87c]/10 rounded-lg -z-10" />
              </div>
            </div>
          </div>
        </div>

        {/* After Death Info */}
        <div className="mt-20 p-8 lg:p-10 bg-gradient-to-r from-[#c9a87c]/10 to-transparent border border-[#c9a87c]/20 rounded-lg">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <h3 className="font-serif text-xl lg:text-2xl text-[#faf9f7] mb-2">
                What Happens After You Pass?
              </h3>
              <p className="font-sans text-[#faf9f7]/70">
                Your beneficiaries simply obtain a certified death certificate, record it with the county, 
                and update property records. The transfer is typically much faster and simpler than a court-supervised process.
              </p>
            </div>
            <a
              href="#contact"
              className="btn-elegant inline-flex items-center justify-center px-6 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-medium tracking-wide rounded-sm hover:bg-[#d4b896] whitespace-nowrap"
            >
              Get Started Today
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
